import { Component, ElementRef, OnInit } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { ActivatedRoute } from '@angular/router'
import { IonRouterOutlet } from '@ionic/angular'
import { Page } from 'src/app/core/page'
import { DailyLogPost, DailyLogTemplateExampleBanner, DailyLogTemplateListItem } from 'src/app/interfaces/daily-log'
import { DailyLogService } from 'src/app/services/daily-log/daily-log.service'
import { GeneralService } from 'src/app/services/general/general.service'
import { GiphyService } from 'src/app/services/giphy/giphy.service'
import { LocaleService } from 'src/app/services/locale/locale-service'
import { LogService } from 'src/app/services/log/log.service'
import { ModalService } from 'src/app/services/modal/modal.service'
import { RouterService } from 'src/app/services/router/router.service'
import { SnackbarService } from 'src/app/services/snackbar/snackbar.service'
import { UserService } from 'src/app/services/user/user.service'
import { customLocalStorage } from 'src/utils/utils'
import { DailyLogTemplateNameInputModalComponent } from './daily-log-template-name-input-modal/daily-log-template-name-input-modal.component'
import { DailyLogTemplateSelectModalComponent } from './daily-log-template-select-modal/daily-log-template-select-modal.component'

export const CREATED_DAILY_LOGS_STORAGE_KEY = 'created_daily_logs'
export const DAILY_LOG_TEMPLATE_TUTORIAL_SHOWN_STORAGE_KEY = 'daily_log_template_tutorial_shown'

@Component({
  selector: 'app-daily-log-template',
  templateUrl: './daily-log-template.component.html',
  styleUrls: ['./daily-log-template.component.scss'],
})
export class DailyLogTemplateComponent extends Page implements OnInit {
  constructor(
    protected generalService: GeneralService,
    protected routerService: RouterService,
    protected routerOutlet: IonRouterOutlet,
    protected modalService: ModalService,
    protected title: Title,
    protected logService: LogService,
    protected localeService: LocaleService,
    private dailyLogService: DailyLogService,
    private userService: UserService,
    private snackbarService: SnackbarService,
    private activatedRoute: ActivatedRoute,
    private giphyService: GiphyService,
    private elementRef: ElementRef
  ) {
    super(generalService, routerService, routerOutlet, modalService, title, logService, localeService)
  }
  screenName: string = 'daily-log-template'
  templateList: DailyLogTemplateListItem[] = []
  bigTemplate: DailyLogTemplateListItem | undefined
  bannerState: 'guide' | 'install' = 'guide'
  isLoggedIn = this.userService.isLoggedIn
  exampleBannerData: DailyLogTemplateExampleBanner | undefined
  isTutorialMode = false
  enableTutorial = this.logService.exp.exp078 === 1
  images: any[] = []
  private tutorialExperimentSubscription = this.logService.exp.getExpValueChangedSubject().subscribe((res) => {
    const exp078 = res.find((exp) => exp.expNo === 78)
    if (exp078) {
      this.enableTutorial = exp078.value === 1
      this.initTutorial()
    }
  })
  private cursor: string | null | undefined
  private isLoading = false

  ngOnInit() {
    this.logService.dailyLog.logDailyLogTemplateScreenViewed()
    this.initTutorial()
    this.fetchTemplateList()
    this.activatedRoute.queryParams.subscribe((params) => {
      if (params?.templateId && this.userService.isLoggedIn) {
        this.createDailyLogByTemplate(params.templateId)
        // query parameter 삭제 (뒤로갔을 때 또 생성될 수 있어서)
        const url = new URL(location.href)
        url.search = '' // Clear the query parameters
        history.replaceState(null, '', url.href)
      }
    })
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy()
    this.tutorialExperimentSubscription.unsubscribe()
  }

  async onClickTemplate(template: DailyLogTemplateListItem): Promise<void> {
    this.logService.dailyLog.logDailyLogTemplateTemplateClicked(template.id, this.userService.isLoggedIn)
    if (this.userService.isLoggedIn) {
      this.createDailyLogByTemplate(template.id)
    } else {
      const nameInputModal = await this.modalService.openBottomSheet(DailyLogTemplateNameInputModalComponent)
      await nameInputModal.present()
      const nameInputRes = await nameInputModal.onWillDismiss()
      if (nameInputRes && nameInputRes.data) {
        if (this.userService.uuid) {
          this.createDailyLogByTemplate(template.id, {
            userIdentifier: this.userService.uuid,
            nickname: nameInputRes.data,
          })
        } else {
          this.snackbarService.openSnackBar(SnackbarService.ERROR)
        }
      }
    }
  }

  onClickCloseGuideBanner(): void {
    this.bannerState = 'install'
  }

  onClickInstallApp(): void {}

  /**
   * 다른 유저 결과물 구경하기 클릭시
   * 일단 static하게 박아둠. 추후 변경 필요
   */
  onClickWatchOtherResult(bannerData: DailyLogTemplateExampleBanner): void {
    this.logService.dailyLog.logDailyLogTemplateExampleBannerClicked()
    this.routerService.navigate(`daily-log/${bannerData.exampleId}`, {
      queryParams: { showTutorial: this.isTutorialMode, disableExitTutorial: this.isTutorialMode },
    })
    this.finishTutorial(false)
  }

  onClickTutorialBackground(): void {
    this.finishTutorial(true)
  }

  private finishTutorial(isCanceled: boolean): void {
    if (this.isTutorialMode) {
      this.logService.dailyLog.logDailyLogTemplateTutorialFinished(isCanceled)
      customLocalStorage.setItem(DAILY_LOG_TEMPLATE_TUTORIAL_SHOWN_STORAGE_KEY, 'true')
      this.isTutorialMode = false
    }
  }

  private async createDailyLogByTemplate(
    templateId: number,
    anonymous?: { userIdentifier: string; nickname: string }
  ): Promise<void> {
    const createdDailyLogs: DailyLogPost[] = JSON.parse(
      customLocalStorage.getItem(CREATED_DAILY_LOGS_STORAGE_KEY) ?? '[]'
    )
    const createdSameTemplates = createdDailyLogs.filter((dailyLog) => dailyLog.template?.id === templateId)
    // 이미 생성된 템플릿이 있는지 확인
    if (createdSameTemplates.length > 0) {
      const selectModal = await this.modalService.openBottomSheet(DailyLogTemplateSelectModalComponent)
      await selectModal.present()
      const { data } = await selectModal.onWillDismiss()
      if (data?.type === 'move') {
        const post = createdSameTemplates[0]
        this.routerService.navigate(`daily-log/${post.id}`, { state: { dailyLog: post } })
      }
      if (data?.type === 'create') {
        // 이하 생성 코드 수행
      } else {
        // 생성 아닐경우 return
        return
      }
    }
    this.dailyLogService.createDailyLogByTemplate(templateId, anonymous).subscribe((res) => {
      customLocalStorage.setItem(CREATED_DAILY_LOGS_STORAGE_KEY, JSON.stringify([res.data, ...createdDailyLogs]))
      this.routerService.navigate(`daily-log/${res.data.id}`, { state: { dailyLog: res.data } })
    })
  }

  private fetchTemplateList(): void {
    if (this.isLoading || this.cursor === null) return
    this.isLoading = true
    this.dailyLogService.fetchDailyLogTemplateList(this.cursor ?? null).subscribe(
      (res) => {
        this.isLoading = false
        if (!this.bigTemplate) {
          this.bigTemplate = res.data.templates.find((item: DailyLogTemplateListItem) => item.isBigBanner)
        }
        this.templateList = this.templateList
          .concat(res.data.templates)
          .filter((item) => item.id !== this.bigTemplate?.id)
        this.cursor = res.cursor ?? null
        if (this.exampleBannerData === undefined && res.data.banner?.exampleId) {
          this.exampleBannerData = res.data.banner
        }
      },
      (error) => {
        this.isLoading = false
        console.error(error)
      }
    )
  }

  private initTutorial(): void {
    if (this.enableTutorial) {
      this.isTutorialMode = customLocalStorage.getItem(DAILY_LOG_TEMPLATE_TUTORIAL_SHOWN_STORAGE_KEY) !== 'true'
    } else {
      this.isTutorialMode = false
    }
  }
}
